import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  TableOptions,
  useReactTable
} from '@tanstack/react-table';

type NativeTableOptions<T> = Omit<TableOptions<T>, 'getCoreRowModel' | 'getPaginationRowModel' | 'getSortedRowModel'> & {
  getCoreRowModel?: TableOptions<T>['getCoreRowModel'];
  getPaginationRowModel?: TableOptions<T>['getPaginationRowModel'];
  getSortedRowModel?: TableOptions<T>['getSortedRowModel'];
};

/**
 * A wrapper around useReactTable that automatically configures commonly needed row models:
 * - Core row model (required)
 * - Pagination row model (for client-side pagination)
 * - Sorted row model (for client-side sorting)
 *
 * You can still override any of these by passing them explicitly in options.
 * Default page size is 20 items per page.
 *
 * @example
 * const table = useNativeTable({
 *   columns,
 *   data,
 *   // pagination and sorting automatically configured!
 * });
 */
export function useNativeTable<T>( options: NativeTableOptions<T> ) {
  return useReactTable( {
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...options,
    // Merged after the options spread so callers that pass a partial
    // initialState (e.g. only columnPinning) keep the pageSize default.
    initialState: {
      pagination: {
        pageSize: 20
      },
      ...options.initialState
    }
  } );
}